'use client';

import { type ComponentPropsWithoutRef, memo, type ReactNode, useCallback } from 'react';

import { ChevronDownIcon, ChevronRightIcon } from '@air/design-icons';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export type CaretButtonProps = {
  className?: string;
  onClick: () => void;
  open: boolean;
};

/** Caret button for expanding/collapsing tree items */
const CaretButton = memo(({ className, onClick, open }: CaretButtonProps) => {
  const Icon = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    <button
      aria-label={open ? 'Collapse' : 'Expand'}
      className={cn(
        'relative z-20 -mx-0.5 items-center justify-center rounded-4 p-0.5 text-neutral-10 hover:bg-neutral-2 hover:text-neutral-12',
        className ?? 'flex',
      )}
      onClick={onClick}
      type="button"
    >
      <Icon size="sm" />
    </button>
  );
});

CaretButton.displayName = 'CaretButton';

const TREE_ITEM_DEPTH_INDENT_PX = 4;
const TREE_ITEM_BASE_INDENT_PX = 6;

/** Left inset for a row at `depth`. Shared with every row a tree level renders, so they line up. */
export const getTreeItemPaddingLeft = (depth: number) =>
  `${depth * TREE_ITEM_DEPTH_INDENT_PX + TREE_ITEM_BASE_INDENT_PX}px`;

export const treeItemVariants = tailwindVariants({
  base: 'group relative flex h-9 items-center gap-3 rounded-6 px-3 font-medium text-14 text-neutral-11',
  defaultVariants: {
    selected: false,
  },
  variants: {
    disabled: {
      false: '',
      true: 'cursor-not-allowed opacity-50',
    },
    selected: {
      false: 'hover:bg-neutral-3',
      true: 'bg-neutral-3 text-neutral-12',
    },
  },
});

export type TreeItemVariants = VariantProps<typeof treeItemVariants>;

export type TreeItemProps = Pick<ComponentPropsWithoutRef<'button'>, 'disabled' | 'onClick' | 'onMouseEnter'> &
  TreeItemVariants &
  Pick<ComponentPropsWithoutRef<'div'>, 'children'> & {
    depth: number;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    prefix?: ReactNode;
    suffix?: ReactNode;
    /** When true, shows prefix by default and caret only on hover */
    showCaretOnHover?: boolean;
  };

export const TreeItem = memo(
  ({
    children,
    disabled,
    open = false,
    depth = 0,
    onClick,
    onMouseEnter,
    onOpenChange,
    prefix,
    selected,
    suffix,
    showCaretOnHover = false,
  }: TreeItemProps) => {
    const handleOpenChange = useCallback(() => {
      onOpenChange?.(!open);
    }, [onOpenChange, open]);

    return (
      <div
        className={treeItemVariants({ disabled, selected })}
        style={{
          paddingLeft: getTreeItemPaddingLeft(depth),
        }}
      >
        <button
          aria-label={typeof children === 'string' ? children : 'Tree item'}
          className="absolute inset-0 z-10"
          disabled={disabled}
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          type="button"
        />

        <div className="flex shrink-0 items-center gap-1 p-0.5">
          {showCaretOnHover ? (
            <div className="relative flex size-6 items-center justify-center">
              {prefix && <div className="flex items-center justify-center group-hover:hidden">{prefix}</div>}
              <CaretButton
                className={prefix ? 'hidden group-hover:flex' : 'flex'}
                onClick={handleOpenChange}
                open={open}
              />
            </div>
          ) : (
            <>
              <CaretButton className="flex" onClick={handleOpenChange} open={open} />
              {prefix && <div className="flex items-center justify-center p-0.5">{prefix}</div>}
            </>
          )}
        </div>

        <span className="truncate">{children}</span>

        {suffix && <div className="flex shrink-0 items-center justify-center p-px">{suffix}</div>}
      </div>
    );
  },
);

TreeItem.displayName = 'TreeItem';
